import { type SyntheticEvent, useState } from "react";

import { t } from "i18next";

import { MenuItem, SxProps, TextField, Theme } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { DateRangePresetSet } from "@/src/components/organisms/AdminDataTable/dateRangeFilter";
import DateRangeFilterInput from "@/src/components/organisms/AdminDataTable/DateRangeFilterInput";
import { NumberRangePresetSet } from "@/src/components/organisms/AdminDataTable/numberRangeFilter";
import NumberRangeFilterInput from "@/src/components/organisms/AdminDataTable/NumberRangeFilterInput";
import { Column } from "@/src/components/organisms/AdminDataTable/reactTable";

export interface FilterOption {
  label: string;
  value: string;
}

export interface ColumnFilterMeta {
  filterVariant?: "text" | "select" | "dateRange" | "numberRange";
  filterOptions?: FilterOption[];
  filterLabel?: string;
  dateRangePresets?: DateRangePresetSet;
  numberRangePresets?: NumberRangePresetSet;
  headerTooltip?: string;
}

const filterFieldSx: SxProps<Theme> = {
  backgroundColor: theme.palette.background.paper,
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper
  }
};

interface ColumnFilterInputProps<TData extends object> {
  column: Column<TData, unknown>;
}

const ColumnFilterInput = <TData extends object>(
  props: ColumnFilterInputProps<TData>
) => {
  const { column } = props;
  const meta = column.columnDef.meta as ColumnFilterMeta | undefined;
  const variant = meta?.filterVariant ?? "text";
  const label = meta?.filterLabel ?? "";

  const initial = column.getFilterValue();
  const [value, setValue] = useState(
    typeof initial === "string" ? initial : ""
  );

  const handleChange = (next: string) => {
    setValue(next);
    column.setFilterValue(next || undefined);
  };

  const stopPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  if (variant === "dateRange") {
    return (
      <DateRangeFilterInput
        column={column}
        presetSet={meta?.dateRangePresets}
      />
    );
  }

  if (variant === "numberRange") {
    return (
      <NumberRangeFilterInput
        column={column}
        presetSet={meta?.numberRangePresets}
      />
    );
  }

  if (variant === "select" && meta?.filterOptions) {
    return (
      <TextField
        select
        size="small"
        fullWidth
        value={value}
        label={label}
        onChange={(e) => handleChange(e.target.value)}
        onClick={stopPropagation}
        sx={filterFieldSx}
        slotProps={{
          select: { displayEmpty: true },
          inputLabel: { shrink: true }
        }}
      >
        <MenuItem value="">
          <em>{t("admin.dataTable.filters.all")}</em>
        </MenuItem>
        {meta.filterOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <TextField
      size="small"
      fullWidth
      value={value}
      label={label}
      onChange={(e) => handleChange(e.target.value)}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      sx={filterFieldSx}
      slotProps={{
        inputLabel: { shrink: true }
      }}
    />
  );
};

export default ColumnFilterInput;
